<template>
  <MTPage>
    
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <MTSectionHeader>Continue Learning</MTSectionHeader>
        <p v-if="engine.lesson.value" class="text-[var(--mt-text-muted)]">
          {{ engine.lesson.value.moduleTitle }}
          <span class="opacity-60"> → </span>
          {{ engine.lesson.value.unitTitle }}
          <span class="opacity-60"> → </span>
          <router-link
            :to="`/student/${childId}/learn/${engine.lesson.value.id}`"
            class="font-medium text-blue-600 hover:underline"
          >
            {{ engine.lesson.value.title }}
          </router-link>
        </p>
      </div>

      <MTButton variant="outline" size="md" @click="goDashboard">
        Back to Dashboard
      </MTButton>
    </div>
    <div class="max-w-4xl mx-auto space-y-6">
      <LessonIntro v-if="showLessonIntro"
          :content="engine.lesson.value?.learningContent"
          @startPractice="startPractice"
        />
      <LessonCompleteCard
        v-if="engine.lessonReady.value && engine.lesson.value"
        :lessonTitle="engine.lesson.value.title"
        :band="engine.lessonBand.value"
        @next="goToNextLesson"
        @repeat="repeatLesson"
      />

      <MTCard v-else-if="practiceStarted" class="p-6">
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex items-center gap-2">
          <!-- Adaptive feedback -->
          <transition name="fade">
            <div
              v-if="engine.adaptiveMessage.value"
              class="mb-4 text-sm font-semibold px-4 py-2 rounded-lg
                     bg-yellow-100 text-yellow-800 inline-flex items-center gap-2"
            >
              {{ engine.adaptiveMessage.value }}
            </div>
          </transition>
            <span
                v-if="skillLabel"
                class="inline-flex items-center gap-1 px-3 py-1 rounded-full
                       text-xs font-semibold bg-blue-100 text-blue-800"
            >
                <MTIcon name="target" :size="14" />
                {{ skillLabel }}
            </span>

            <span
                class="inline-flex items-center gap-1 px-3 py-1 rounded-full
                       text-xs font-semibold text-white"
              :class="difficultyColor(engine.displayDifficulty.value)"
            >
              <MTIcon :name="difficultyIconName(engine.displayDifficulty.value)" :size="14" />
              {{ friendlyDifficulty(engine.displayDifficulty.value) }}
            </span>
          </div>

            <!-- “Need a reminder?” micro-help trigger -->
            <div class="text-xs text-[var(--mt-text-muted)]">
              <span class="text-xs text-[var(--mt-text-muted)]">
              Need help? <button class="underline" @click="showReminder = !showReminder">Quick reminder</button>
            </span>
            </div>
        </div>

          <!-- Optional micro-help panel if you have it -->
          <SkillReminder
            v-if="showReminder && skillHelpContent"
            :skillId="engine.currentSectionGroupId.value"
            :content="skillHelpContent"
            @close="showReminder = false"
          />
        
        <div class="h-3 bg-[var(--mt-track-bg)] rounded-full overflow-hidden mb-5">
          <div class="h-3 bg-blue-600" :style="{ width: progressPercent + '%' }"></div>
        </div>

        <div v-if="engine.currentQuestion.value">
          <QuestionBlock
            :question="engine.currentQuestion.value.question"
            :studentId="childId"
            :lessonId="engine.lesson.value?.id"
            @questionCompleted="onQuestionCompleted"
          />
        </div>

        <!-- Loading state -->
        <div v-else-if="engine.isLoading.value" class="text-center py-10">
          <MTIcon name="loader" :size="32" class="animate-spin text-blue-500 mx-auto mb-3" />
          <p class="text-[var(--mt-text-muted)]">Loading questions...</p>
        </div>

        <!-- No questions found -->
        <div v-else-if="engine.noQuestionsAvailable.value" class="text-center py-10">
          <MTIcon name="alert-circle" :size="48" class="text-amber-500 mx-auto mb-3" />
          <p class="text-lg font-semibold mb-2">No questions available</p>
          <p class="text-[var(--mt-text-muted)] mb-4">
            There are no practice questions for this lesson yet.
          </p>
          <MTButton variant="outline" @click="goDashboard">
            Back to Dashboard
          </MTButton>
        </div>

        <!-- Questions exhausted but mastery not achieved -->
        <div v-else-if="engine.questionsExhausted.value" class="text-center py-10">
          <MTIcon name="trophy" :size="48" class="text-blue-500 mx-auto mb-3" />
          <p class="text-lg font-semibold mb-2">Great effort!</p>
          <p class="text-[var(--mt-text-muted)] mb-4">
            You've completed all available questions for this lesson.
            <br />
            Come back later for more practice.
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <MTButton variant="outline" @click="goDashboard">
              Take a break
            </MTButton>
            <MTButton variant="primary" @click="goToNextLesson">
              Try next lesson
            </MTButton>
          </div>
        </div>

        <!-- Fallback loading state -->
        <div v-else class="text-center py-10">
          <MTIcon name="loader" :size="32" class="animate-spin text-blue-500 mx-auto mb-3" />
          <p class="text-[var(--mt-text-muted)]">Preparing questions...</p>
        </div>

        <div class="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <MTButton variant="outline" size="md" @click="pause">
            Take a break
          </MTButton>
        </div>
      </MTCard>
    </div>
  </MTPage>
</template>

<script setup>
import { computed, ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import MTPage from "@/components/ui/MTPage.vue";
import MTCard from "@/components/ui/MTCard.vue";
import MTButton from "@/components/ui/MTButton.vue";
import MTSectionHeader from "@/components/ui/MTSectionHeader.vue";
import MTIcon from "@/components/ui/MTIcon.vue";

import LessonIntro from "@/components/lesson/LessonIntro.vue";
import SkillReminder from "@/components/lesson/SkillReminder.vue";
import LessonCompleteCard from "@/components/lesson/LessonCompleteCard.vue";
import QuestionBlock from "@/components/questions/QuestionBlock.vue";

import { useContinueLearningEngine } from "@/composables/useContinueLearningEngine";
import { useProgressStore } from "@/stores/progressStore";
import { SECTION_GROUP_HELP } from "@/config/section-group-help";
const route = useRoute();
const router = useRouter();
const progressStore = useProgressStore();

// Local UI state
const practiceStarted = computed(() => !showLessonIntro.value);

const childId = computed(() => route.params.studentId);
const engine = useContinueLearningEngine(childId.value);

const showReminder = ref(false);

// Capture queue length once per session (constant progress display)
const sessionTotal = ref(10);

const progressPercent = computed(() => {
  const total = Math.max(1, sessionTotal.value);
  const done = Math.min(engine.currentIndex.value, total);
  return Math.round((done / total) * 100);
});

const currentSkillBand = computed(() => {
  const sgId = engine.currentSectionGroupId.value;
  if (!sgId) return "beginner";
  return progressStore.getSectionGroupBand(childId.value, sgId);
});

onMounted(async () => {
  await engine.start();
  // if (showLessonIntro === false) practiceStarted.value = true;
  if (engine.queue.value?.length) sessionTotal.value = engine.queue.value.length;
});

watch(

  () => engine.queue.value?.length,
  (len) => {
    if (len && sessionTotal.value === 10) sessionTotal.value = len;
  }
);
/**
 * LessonIntro should show:
 * - lesson exists
 * - lesson has learningContent (or you can show intro anyway)
 * - practice not started
 * - not paused
 * - not completed
 */
const showLessonIntro = computed(() => {
  // if (!engine.lesson.value) return false;
  // if (engine.sessionCompleted.value) return false;
  // if (engine.sessionPaused.value) return false;
  // return !practiceStarted.value;
  //TODO: enable me
  return false;
});

// Skill label from config (preferred) with fallback
import { useCurriculumStore } from "@/stores/curriculumStore";
const curriculumStore = useCurriculumStore();

function getSectionGroupLabel(sgId) {
  // Search all modules/units/lessons for a subsection with this content id
  for (const mod of curriculumStore.modules) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        if (Array.isArray(lesson.subsections)) {
          const sub = lesson.subsections.find(sub => sub.content === sgId);
          if (sub && sub.title) return sub.title;
        }
      }
    }
  }
  return sgId;
}

const skillLabel = computed(() => {
  const sgId = engine.currentSectionGroupId.value;
  return sgId ? getSectionGroupLabel(sgId) : null;
});
const skillHelpContent = computed(() => {
  const sgId = engine.currentSectionGroupId.value;
  return sgId ? SECTION_GROUP_HELP?.[sgId] : null;
});
function startPractice() {
  practiceStarted.value = true;
}

function goDashboard() {
  router.push(`/student/${childId.value}/dashboard`);
}

function pause() {
  engine.sessionPaused.value = true;
  if (engine.lesson.value?.id) progressStore.setContinueLesson(childId.value, engine.lesson.value.id);
  router.push(`/student/${childId.value}/dashboard`);
}

async function goToNextLesson() {
  engine.resetSession();
  await engine.start();
  if (engine.queue.value?.length) sessionTotal.value = engine.queue.value.length;
}

async function repeatLesson() {
  engine.resetSession();
  await engine.start();
  if (engine.queue.value?.length) sessionTotal.value = engine.queue.value.length;
}

async function onQuestionCompleted(result) {
  await engine.submitQuestionResult(result);
}

function friendlyDifficulty(level) {
  return { easy: "Getting Started", medium: "Growing", hard: "Expert" }[level] || "Growing";
}
function difficultyIconName(level) {
  return { easy: "star", medium: "sprout", hard: "crown" }[level] || "sprout";
}
function difficultyColor(level) {
  return { easy: "bg-blue-500", medium: "bg-green-500", hard: "bg-purple-600" }[level] || "bg-gray-400";
}
function bandColor(band) {
  return {
    beginner: "bg-blue-500",
    growing: "bg-green-500",
    secure: "bg-yellow-500",
    confident: "bg-orange-500",
    mastery: "bg-purple-600"
  }[band] || "bg-gray-400";
}
function sgShort(sgId) {
  return (sgId || "").replace(/^WS_/, "").replace(/_/g, " ").slice(0, 22);
}
</script>
